import classNames from 'classnames';
import ConfirmChangesDialog from 'components/organisms/NavigationBlocker/ConfirmChangesDialog';
import { useNavigationBlocker } from 'components/organisms/NavigationBlocker';
import { Button, TextField } from 'components/widgets';
import CopyToClipboardButton from 'components/molecules/CopyToClipboardButton';
import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { updateNavigationSections } from '../../actions/navigation';
import { getCanAccessFeature } from '../../selectors/navigation';
import { getIsAgency } from '../../selectors/user';
import NavigationService from '../../services/navigation';
import { trackMixpanelEvent } from '../../utils/general';
import WarningMessage from '../../components/utilities/WarningMessage';
import TagPicker from './TagPicker';
import DescriptionField from './DescriptionField';
import FieldLabel from './FieldLabel';
import LogoUpload from './LogoUpload';
import WebsiteField from './WebsiteField';
import { TAGS } from '../consts';
import css from './ExternalOrgProfileForm.css';
import service from '../service';

const translationPrefix = 'STATIC.PAGES.EXTERNAL_ORG_PROFILE';
const MAX_TAGS = 2;
const PARTNER_INTEGRATION_PATH = '#/react/integrations';

const hasRequiredFields = ({ website, hasLogo }) => Boolean((website || '').trim()) && Boolean(hasLogo);

const navigationService = new NavigationService();

const ExternalOrgProfileForm = ({ translate, setSavedProfile, shouldRedirectAfterFirstSaveRef }) => {
    const dispatch = useDispatch();
    const isAgency = useSelector(getIsAgency);
    const canAccessIntegration = useSelector(getCanAccessFeature('partners_integration'));
    const { values, dirty, initialValues, setFieldValue, resetForm } = useFormikContext();

    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [logoError, setLogoError] = useState('');
    const [saveMessage, setSaveMessage] = useState(null);
    const [websiteFieldValid, setWebsiteFieldValid] = useState(false);

    const { allowNextNavigation } = useNavigationBlocker(dirty);

    const tagOptions = isAgency ? TAGS.agency : TAGS.partner;
    const nameLabelKey = isAgency ? 'AGENCY_NAME_LABEL' : 'COMPANY_NAME_LABEL';
    const nameNoteKey = isAgency ? 'AGENCY_NAME_NOTE' : 'COMPANY_NAME_NOTE';
    const logoLabelKey = isAgency ? 'AGENCY_LOGO_LABEL' : 'LOGO_LABEL';
    const descriptionLabelKey = isAgency ? 'AGENCY_DESCRIPTION_LABEL' : 'DESCRIPTION_LABEL';
    const descriptionNoteKey = isAgency ? 'AGENCY_DESCRIPTION_NOTE' : 'DESCRIPTION_NOTE';
    const tagsLabelKey = isAgency ? 'SPECIALTIES_LABEL' : 'CATEGORIES_LABEL';
    const tagsNoteKey = isAgency ? 'SPECIALTIES_NOTE' : 'CATEGORIES_NOTE';

    const confirmCancel = () => {
        setShowCancelConfirm(false);
        setLogoError('');
        setSaveMessage(null);
        resetForm();
    };

    const isFormValid =
        hasRequiredFields({
            website: values.website,
            hasLogo: Boolean(values.logoFile || (values.logoUrl || '').trim()),
        }) &&
        websiteFieldValid &&
        !logoError;

    const buildSavedProfile = data => ({
        companyName: data.company_name || '',
        agencyId: data.agency_id || '',
        website: data.website || '',
        description: data.description || '',
        tags: data.tags || [],
        logoUrl: data.logo_url || '',
        logoFile: null,
    });

    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage(null);

        try {
            let updatedLogoUrl = values.logoUrl;

            if (values.logoFile) {
                const data = await service.uploadLogo(values.logoFile);
                updatedLogoUrl = data.logo_url;
            }

            const hasProfileChanges =
                values.website !== initialValues.website ||
                values.description !== initialValues.description ||
                values.tags.length !== initialValues.tags.length ||
                !values.tags.every(t => initialValues.tags.includes(t));

            let updatedProfile;
            if (hasProfileChanges) {
                const data = await service.updateProfile({
                    website: values.website.trim(),
                    description: values.description.trim(),
                    tags: values.tags,
                });
                updatedProfile = buildSavedProfile(data);
            } else {
                updatedProfile = {
                    ...values,
                    logoUrl: updatedLogoUrl,
                    logoFile: null,
                };
            }

            setSavedProfile(updatedProfile);
            resetForm({ values: updatedProfile });

            setSaveMessage({
                type: 'success',
                message: `${translationPrefix}.MESSAGES.${isAgency ? 'AGENCY_SAVE_SUCCESS' : 'SAVE_SUCCESS'}`,
            });

            trackMixpanelEvent(
                isAgency ? 'Agency Onboarding' : 'Partner Onboarding',
                isAgency ? 'Agency Info Saved' : 'Partner Info Saved',
                {
                    website: values.website.trim(),
                    description: values.description.trim(),
                    // Preserve the partner event's original payload key; agency uses its own.
                    [isAgency ? 'specialties_selected' : 'categories_selected']: values.tags,
                }
            );

            // Refresh nav so the sidebar "incomplete" tag clears after a successful save
            // (both org types), and so a partner redirect below reads fresh sections.
            try {
                const sections = await navigationService.getNavigation({ cache: false });
                dispatch(updateNavigationSections(sections));
            } catch {
                // Tolerate refresh failure.
            }

            // Partner-only: after the first successful save, jump to Integrations.
            if (!isAgency && shouldRedirectAfterFirstSaveRef.current && canAccessIntegration) {
                shouldRedirectAfterFirstSaveRef.current = false;
                allowNextNavigation();
                window.location.replace(PARTNER_INTEGRATION_PATH);
                return;
            }
            setIsSaving(false);
        } catch (err) {
            setIsSaving(false);
            setSaveMessage({
                type: 'error',
                message: `${translationPrefix}.MESSAGES.${isAgency ? 'AGENCY_SAVE_ERROR' : 'SAVE_ERROR'}`,
            });
        }
    };

    const handleLogoFileChange = file => {
        setFieldValue('logoFile', file);
        setLogoError('');
    };

    const handleLogoRemove = () => {
        setFieldValue('logoUrl', '');
        setFieldValue('logoFile', null);
        setLogoError('');
    };

    const toggleTag = tag => {
        const { tags } = values;
        if (tags.includes(tag)) {
            setFieldValue(
                'tags',
                tags.filter(t => t !== tag)
            );
        } else if (tags.length < MAX_TAGS) {
            setFieldValue('tags', [...tags, tag]);
        }
    };

    return (
        <>
            {saveMessage && (
                <div className={css.saveMessageContainer}>
                    <WarningMessage
                        type={saveMessage.type}
                        message={saveMessage.message}
                        className={css.saveMessage}
                        showIcon
                        onClose={() => setSaveMessage(null)}
                    />
                </div>
            )}
            <div className={css.form}>
                <div className={css.fieldGroup}>
                    <FieldLabel icon="building" translationKey={`${translationPrefix}.${nameLabelKey}`} />
                    <TextField
                        value={values.companyName}
                        onChange={() => {}}
                        disabled
                        containerClass={css.textFieldContainer}
                    />
                    <p className={css.fieldNote}>
                        <Translate id={`${translationPrefix}.${nameNoteKey}`} />
                    </p>
                </div>
                {isAgency && (
                    <div className={css.fieldGroup}>
                        <FieldLabel icon="id" translationKey={`${translationPrefix}.AGENCY_ID_LABEL`} />
                        <div className={css.idFieldRow}>
                            <TextField
                                value={values.agencyId}
                                onChange={() => {}}
                                disabled
                                containerClass={css.textFieldContainer}
                            />
                            <CopyToClipboardButton
                                textToCopy={values.agencyId}
                                tooltip={`${translationPrefix}.AGENCY_ID_COPY_TOOLTIP`}
                            />
                        </div>
                        <p className={css.fieldNote}>
                            <Translate id={`${translationPrefix}.AGENCY_ID_NOTE`} />
                        </p>
                    </div>
                )}
                <div className={css.fieldGroup}>
                    <WebsiteField
                        onValidityChange={setWebsiteFieldValid}
                        translationPrefix={translationPrefix}
                        containerClass={css.textFieldContainer}
                        translate={translate}
                    />
                </div>
                <div className={css.logoDescriptionRow}>
                    <div className={css.fieldGroup}>
                        <FieldLabel icon="upload" translationKey={`${translationPrefix}.${logoLabelKey}`} required />
                        <p className={css.fieldNote}>
                            <Translate id={`${translationPrefix}.LOGO_NOTE`} />
                        </p>
                        <LogoUpload
                            logoFile={values.logoFile}
                            logoUrl={values.logoUrl}
                            error={logoError}
                            onFileChange={handleLogoFileChange}
                            onValidationError={setLogoError}
                            onRemove={handleLogoRemove}
                        />
                    </div>
                    <div className={css.fieldGroup}>
                        <FieldLabel
                            icon="file-description"
                            translationKey={`${translationPrefix}.${descriptionLabelKey}`}
                        />
                        <p className={css.fieldNote}>
                            <Translate id={`${translationPrefix}.${descriptionNoteKey}`} />
                        </p>
                        <DescriptionField
                            value={values.description}
                            onChange={value => setFieldValue('description', value)}
                        />
                    </div>
                </div>
                <div className={classNames(css.fieldGroup, css.tagsFieldGroup)}>
                    <FieldLabel icon="layout-grid" translationKey={`${translationPrefix}.${tagsLabelKey}`} />
                    <p className={css.fieldNote}>
                        <Translate id={`${translationPrefix}.${tagsNoteKey}`} data={{ max: MAX_TAGS }} />
                    </p>
                    <TagPicker options={tagOptions} selected={values.tags} max={MAX_TAGS} onToggle={toggleTag} />
                </div>
            </div>
            <div className={css.footer}>
                {(!isFormValid || !dirty) && (
                    <span className={css.footerNote}>
                        <Translate
                            id={
                                !isFormValid
                                    ? `${translationPrefix}.REQUIRED_FIELDS_NOTE`
                                    : `${translationPrefix}.NO_CHANGES_NOTE`
                            }
                        />
                    </span>
                )}
                {dirty && (
                    <Button
                        type="flat"
                        onClick={() => setShowCancelConfirm(true)}
                        disabled={isSaving}
                        text="STATIC.BUTTONS.CANCEL"
                    />
                )}
                <Button
                    type="primary"
                    onClick={handleSave}
                    disabled={!dirty || !isFormValid || isSaving}
                    showSpinner={isSaving}
                    text="STATIC.BUTTONS.SAVE"
                />
            </div>
            <ConfirmChangesDialog
                open={showCancelConfirm}
                onAccept={confirmCancel}
                onReject={() => setShowCancelConfirm(false)}
            />
        </>
    );
};

ExternalOrgProfileForm.propTypes = {
    translate: PropTypes.func.isRequired,
    setSavedProfile: PropTypes.func.isRequired,
    shouldRedirectAfterFirstSaveRef: PropTypes.shape({ current: PropTypes.bool }).isRequired,
};

export default ExternalOrgProfileForm;
